import * as React from 'react';
import { Route, useParams } from 'react-router-dom';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { animated, useSpring } from '@react-spring/web';
import loadable from '@loadable/component';

import { useQuery } from '@apollo/client';

import MDSpinner from 'react-md-spinner';

import NoMatch from './NoMatch';

import { GET_PROPERTY_QUERY } from '../../../client/__graphql__/queries/getProperty';
import {
  GetProperty,
  GetPropertyVariables,
} from '../../__types__/generated/GetProperty';

import { Button } from '../ui/Button';
import { GlobalStyles } from '../../../client/GlobalStyles';
import { theme } from '../../../client/theme/thems/nybygg_dark_old';
import NybyggProfile from './NybyggProfile';

const Preview: React.FC = () => {
  const params = useParams<{ sguid: string }>();
  const [showIsPreview, setShowIsPreview] = React.useState(true);

  const { data, loading, error } = useQuery<GetProperty, GetPropertyVariables>(
    GET_PROPERTY_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: { input: { sguid: params?.sguid, preview: true } },
    }
  );

  const animatedIsPreviewProps = useSpring({
    delay: 500,
    from: { opacity: 0, transform: 'translate3d(0, 20px, 0)' },
    to: {
      opacity: showIsPreview && !loading ? 1 : 0,
      transform:
        showIsPreview && !loading
          ? 'translate3d(0, 0px, 0)'
          : 'translate3d(0, 20px, 0)',
    },
  });

  if (error?.graphQLErrors?.length && error.graphQLErrors?.length > 0) {
    if (error.graphQLErrors[0]?.extensions?.code === '404') {
      return <NoMatch />;
    }
  }

  if (!data || loading) {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <PreviewGlobalStyles />
        <PreviewLoading>
          <PreviewLoadingContent>
            <div className="spinner">
              <MDSpinner size={30} singleColor="#e8c893" />
            </div>
            <div className="text-content">
              <h1 style={{ color: '#fff' }}>Laster forhåndsvisning</h1>
              <h3 style={{ color: '#fff' }}>Vennligst vent..</h3>
            </div>
          </PreviewLoadingContent>
        </PreviewLoading>
      </ThemeProvider>
    );
  } else {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <IsPreviewTab style={animatedIsPreviewProps}>
          <span className="label">
            Forhåndsvisning av komplett digital salgsoppgave
          </span>
          <Button
            colorVariant="primary"
            variant="outline"
            onClick={() => {
              setShowIsPreview(false);
            }}
          >
            Lukk
          </Button>
        </IsPreviewTab>
        <Route
          path="/:page(preview)/:sguid"
          render={(props) => <NybyggProfile preview={true} {...props} />}
        />
      </ThemeProvider>
    );
  }

  return null;
};

export default Preview;

const PreviewLoading = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgb(0, 0, 0);
`;

const PreviewLoadingContent = styled.div`
  display: grid;
  grid-template-columns: max-content max-content;
  grid-column-gap: 20px;
  align-items: center;

  .text-content {
    h1 {
      margin-bottom: 0;
      font-size: 26px;
      font-weight: 300;
    }
    h3 {
      margin-top: 5px;
      font-size: 20px;
      font-weight: 300;
    }

    @media all and (max-width: 500px) {
      h1 {
        font-size: 20px;
      }
      h3 {
        font-size: 16px;
      }
    }
  }
`;

const IsPreviewTab = styled(animated.div)`
  position: fixed;
  bottom: 0;
  padding: 20px;
  width: 100%;
  display: grid;
  align-items: center;
  grid-template-columns: 1fr max-content max-content;
  grid-column-gap: 20px;
  z-index: 9999;
  background: rgb(40, 40, 40);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);

  .label {
    display: block;
    font-family: 'DomaineDisp', sans-serif;
    font-weight: 200;
    font-size: 22px;
    color: #e8c893;
  }

  @media all and (max-width: 500px) {
    .label {
      font-size: 16px;
    }
  }
`;

const PreviewGlobalStyles = createGlobalStyle`
html, body {
  height: 100%;
}
  #root {
    height: 100%;
  }
`;
